<script setup lang="ts">
import { ChevronDown, ChevronUp } from 'lucide-vue-next'
import { useFlowEditorContext } from '../composables/useFlowEditor'

/** Collapsible node palette; tiles add a node on click or by dragging onto the canvas. */
const editor = useFlowEditorContext()
const { paletteCollapsed } = editor.ui
const { groups: flowPaletteGroups, disabledIds: paletteItemDisabledIds } = editor.palette
const { addNodeFromPalette } = editor.editing
const { onPaletteDragStart } = editor.pointer
</script>

<template>
  <section
    class="flow-bottom-palette"
    :class="{ 'flow-bottom-palette--collapsed': paletteCollapsed }"
  >
    <div class="palette-header">
      <span class="palette-header__title">{{ $t('flow.nodePalette') }}</span>
      <button
        type="button"
        class="panel-collapse-btn"
        :title="paletteCollapsed ? $t('flow.expandPalette') : $t('flow.collapsePalette')"
        @click="paletteCollapsed = !paletteCollapsed"
      >
        <ChevronDown
          v-if="!paletteCollapsed"
          style="width: 0.9rem; height: 0.9rem"
          aria-hidden="true"
        />
        <ChevronUp v-else style="width: 0.9rem; height: 0.9rem" aria-hidden="true" />
      </button>
    </div>
    <div v-show="!paletteCollapsed" class="palette-groups">
      <div v-for="group in flowPaletteGroups" :key="group.label" class="palette-group">
        <div class="palette-group__label">{{ group.label }}</div>
        <ul class="palette-list">
          <li v-for="item in group.items" :key="item.id">
            <button
              type="button"
              class="palette-tile"
              :class="{ 'palette-tile--disabled': paletteItemDisabledIds.has(item.id) }"
              :disabled="paletteItemDisabledIds.has(item.id)"
              :draggable="!paletteItemDisabledIds.has(item.id)"
              :title="paletteItemDisabledIds.has(item.id) ? $t('flow.alreadyOnCanvas') : undefined"
              @click="addNodeFromPalette(item)"
              @dragstart="onPaletteDragStart(item, $event)"
            >
              <span class="tile-icon" aria-hidden="true">
                <component :is="item.icon" />
              </span>
              <div class="tile-body">
                <strong class="tile-label">{{ item.label }}</strong>
                <p class="tile-description">{{ item.description }}</p>
              </div>
            </button>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<style scoped src="./styles/panelCollapse.css"></style>

<style scoped>
.flow-bottom-palette {
  border-top: 1px solid var(--pl-color-border-subtle, #e2e8f0);
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.palette-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.3rem 0.5rem 0.3rem 0.6rem;
  flex: 0 0 auto;
  min-height: 36px;
}

.palette-header__title {
  font-size: 0.72rem;
  font-weight: 650;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}

.palette-groups {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0.4rem 0.4rem 0.4rem;
}

.palette-groups {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.75rem 1rem;
  align-items: start;
  min-width: 0;
  min-height: 0;
}

.palette-group {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;
}

.palette-group__label {
  font-size: 0.7rem;
  font-weight: 650;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  padding: 0 0.1rem;
  white-space: nowrap;
}

.palette-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.35rem;
}

.palette-list > li {
  display: flex;
  flex-direction: column;
}

.palette-tile {
  width: 100%;
  height: 3.1rem;
  border: 1px solid #cbd5f5;
  background: #fff;
  border-radius: 0.65rem;
  padding: 0 0.55rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.35rem;
  cursor: pointer;
  text-align: left;
  min-width: 0;
  overflow: hidden;
}

.palette-tile:hover:not(:disabled) {
  border-color: #a5b4fc;
  background: #f8fafc;
}

.palette-tile--disabled,
.palette-tile:disabled {
  opacity: 0.38;
  cursor: not-allowed;
  pointer-events: none;
}

.tile-icon {
  width: 1.75rem;
  height: 1.75rem;
  border: 1px solid #cbd5f5;
  border-radius: 0.65rem;
  background: #eef2ff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  color: #0f172a;
}

.tile-icon :deep(svg) {
  width: 70%;
  height: 70%;
}

.tile-body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  flex: 1;
}

.tile-label {
  margin: 0;
  font-size: 0.8rem;
  font-weight: 650;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tile-description {
  margin: 0;
  color: #475569;
  font-size: 0.7rem;
  line-height: 1.25;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@media (max-width: 1024px) {
  .palette-list {
    grid-template-columns: repeat(2, minmax(110px, 1fr));
  }
}
</style>
